
import { Stack, Typography, Box, Chip, Button } from '@mui/material';
import React, { useState } from 'react';
import { useGetCardOwnerProcedureSupplyUsageDataQuery } from '../../api/cardSlice';
import ItemDetails from '../Analytics/ItemDetails';
import { NumericFormat } from 'react-number-format';
import ToggleButtons from '../CustomComponents/ToggleButtons';
import { OpenInNew } from '@mui/icons-material';
import { useParams } from 'react-router';

function CardUsageComparison({ selectedPhysicians = [], medianSpendA = 0, medianSpendB = 0 }) {
    const [selectedItem, setSelectedItem] = useState(null);
    const [metric, setMetric] = useState('cost');
    const { procedureId } = useParams();
    const firstPhysicianId = selectedPhysicians.length > 0 ? selectedPhysicians[0].id : null;
    const secondPhysicianId = selectedPhysicians.length > 1 ? selectedPhysicians[1].id : null;

    const { data: firstPhysicianData, isFetching: isFetchingFirstPhysician } = useGetCardOwnerProcedureSupplyUsageDataQuery(
        { physicianId: firstPhysicianId, procedureId: procedureId },
        { skip: !firstPhysicianId || !procedureId }
    );
    const { data: secondPhysicianData, isFetching: isFetchingSecondPhysician } = useGetCardOwnerProcedureSupplyUsageDataQuery(
        { physicianId: secondPhysicianId, procedureId: procedureId },
        { skip: !secondPhysicianId || !procedureId }
    );

    const isFetching = isFetchingFirstPhysician || isFetchingSecondPhysician;

    if (!firstPhysicianId || !secondPhysicianId) {
        return <Typography>Select two physicians from the graph to compare usage data.</Typography>;
    }
    if (isFetching) {
        return <Typography>Loading usage data...</Typography>;
    }
    if (!firstPhysicianData || !secondPhysicianData) {
        return <Typography>Unable to load usage data for one or both physicians.</Typography>;
    }

    // Compare items by average cost difference
    const usageA = firstPhysicianData ?? {};
    const usageB = secondPhysicianData ?? {};

    // Re-index by item ID so items are correctly matched across both physicians
    const indexedA = Object.values(usageA).reduce((acc, entry) => {
        if (entry?.item?.id) {
            acc[entry.item.id] = entry;
        }
        return acc;
    }, {});
    const indexedB = Object.values(usageB).reduce((acc, entry) => {
        if (entry?.item?.id) {
            acc[entry.item.id] = entry;
        }
        return acc;
    }, {});

    const allItemIds = Array.from(new Set([
        ...Object.keys(indexedA),
        ...Object.keys(indexedB)
    ]));
    // Build comparison list
    const comparison = allItemIds.map(itemId => {
        const a = indexedA[itemId] || {};
        const b = indexedB[itemId] || {};
        const itemA = a.item || null;
        const itemB = b.item || null;
        return {
            itemId,
            name: itemA?.name || itemA?.external_name || itemB?.name || itemB?.external_name || itemId,
            item: itemA || itemB,
            costA: a.average_cost || 0,
            costB: b.average_cost || 0,
            qtyA: a.average_quantity || 0,
            qtyB: b.average_quantity || 0,
            diff: Math.abs((a.average_cost || 0) - (b.average_cost || 0))
        };
    });
    comparison.sort((a, b) => b.diff - a.diff);
    const topComparison = comparison.slice(0, 10);

    // Get physician names from selectedPhysicians
    const physicianA = selectedPhysicians[0]?.name || 'Physician A';
    const physicianB = selectedPhysicians[1]?.name || 'Physician B';

    // Helper to extract last name for display
    function getDrLastName(name) {
        if (!name) return '';
        const parts = name.trim().split(' ');
        return parts.length > 1 ? `Dr. ${parts[parts.length - 1]}` : `Dr. ${name}`;
    }

    const maxDiff = topComparison[0]?.diff || 1;
    const spendDiff = Math.abs(medianSpendA - medianSpendB);
    const higherPhysician = medianSpendA > medianSpendB ? getDrLastName(physicianA) : getDrLastName(physicianB);

    function getDirectionLabel(item) {
        if (item.costA === item.costB) {
            return 'No difference';
        }
        return item.costA > item.costB ? `${getDrLastName(physicianA)} higher` : `${getDrLastName(physicianB)} higher`;
    }

    function getSeverityColor(item) {
        const ratio = item.diff / maxDiff;
        if (ratio > 0.66) return 'error.main';
        if (ratio > 0.33) return 'warning.main';
        return 'success.main';
    }

    function handleViewCard(cardId) {
        window.open(`/cards/${cardId}`, '_blank');
    }

    return (
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Stack spacing={2} backgroundColor="white" p={2} borderRadius={2} boxShadow={2} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="heavy">Usage Comparison</Typography>
                    <Box sx={{ '& .MuiToggleButton-root': { whiteSpace: 'nowrap' } }}>
                        <ToggleButtons
                            options={[
                                { label: 'Avg Spend', value: 'cost' },
                                { label: 'Avg Quantity', value: 'quantity' },
                            ]}
                            toggleValue={metric}
                            setToggleValue={setMetric}
                        />
                    </Box>
                </Stack>

                {/* Typical spend and difference */}
                <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, backgroundColor: 'background.darkGray', textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{getDrLastName(physicianA)} — Typical Spend</Typography>
                        <Typography variant="heavy" display="block">
                            <NumericFormat value={medianSpendA / 100} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                        </Typography>
                        { // Temporarily commenting out this button and the one below until we have better restricted card access for inventory management and value analysis users. 
                        /* <Typography
                            variant="caption"
                            color="primary.main"
                            onClick={() => handleViewCard(firstCardId)}
                            sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.25, mt: 0.5 }}
                        >
                            View Card <OpenInNew sx={{ fontSize: 12 }} />
                        </Typography> */}
                    </Box>
                    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, backgroundColor: 'background.darkGray', textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{getDrLastName(physicianB)} — Typical Spend</Typography>
                        <Typography variant="heavy" display="block">
                            <NumericFormat value={medianSpendB / 100} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                        </Typography>
                        {/* <Typography
                            variant="caption"
                            color="primary.main"
                            onClick={() => handleViewCard(secondCardId)}
                            sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.25, mt: 0.5 }}
                        >
                            View Card <OpenInNew sx={{ fontSize: 12 }} />
                        </Typography> */}
                    </Box>
                    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, backgroundColor: 'background.darkGray', textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Overall Difference</Typography>
                        <Typography variant="heavy" display="block" color={spendDiff > 0 ? 'error.main' : 'success.main'}>
                            <NumericFormat value={spendDiff / 100} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                        </Typography>
                        { spendDiff > 0 && (
                            <Typography variant="caption" color="text.secondary">{higherPhysician} higher</Typography>
                        )}
                    </Box>
                </Stack>

                <Box>
                    {/* Header row 1: labels */}
                    <Stack direction="row" spacing={2} sx={{ fontWeight: 600, mb: 0 }}>
                        <Box width="30%" />
                        <Box width="40%" textAlign="center">
                            <Typography fontWeight={600}>{metric === 'cost' ? 'Avg Spend' : 'Avg Quantity'}</Typography>
                        </Box>
                        <Box width="30%" textAlign="center">Difference</Box>
                    </Stack>
                    {/* Header row 2: physician names */}
                    <Stack direction="row" spacing={2} sx={{ fontWeight: 600, mb: 0.5 }}>
                        <Box width="30%" />
                        <Box width="20%" textAlign="center">{getDrLastName(physicianA)}</Box>
                        <Box width="20%" textAlign="center">{getDrLastName(physicianB)}</Box>
                        <Box width="30%" />
                    </Stack>
                    {topComparison.map((item, index) => (
                        <Box
                            key={item.itemId}
                            onClick={() => setSelectedItem(selectedItem?.itemId === item.itemId ? null : item)}
                            sx={{
                                borderRadius: 2,
                                boxShadow: 2,
                                mb: 2,
                                p: 2,
                                backgroundColor: selectedItem?.itemId === item.itemId ? 'background.selectedRow' : 'background.card',
                                cursor: 'pointer',
                                border: selectedItem?.itemId === item.itemId ? '2px solid' : '2px solid transparent',
                                borderColor: selectedItem?.itemId === item.itemId ? 'primary.main' : 'transparent',
                                borderLeft: '6px solid',
                                borderLeftColor: getSeverityColor(item),
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box width="30%">
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                                        <Chip label={`#${index + 1}`} size="small" sx={{ height: 20 }} />
                                        <Chip label={getDirectionLabel(item)} size="small" variant="outlined" sx={{ height: 20 }} />
                                    </Stack>
                                    <Typography fontSize="14px" variant="heavy">{item.name}</Typography>
                                    {item.item && item.item.vendor && (
                                        <>
                                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                            Vendor: {item.item.vendor.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                            Category: {item.item.category ? item.item.category : item.item.external_category}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                                <Box width="20%" textAlign="center">
                                    {metric === 'cost'
                                        ? <NumericFormat value={item.costA/100} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                                        : <NumericFormat value={item.qtyA} displayType="text" thousandSeparator decimalScale={2} fixedDecimalScale />
                                    }
                                </Box>
                                <Box width="20%" textAlign="center">
                                    {metric === 'cost'
                                        ? <NumericFormat value={item.costB/100} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                                        : <NumericFormat value={item.qtyB} displayType="text" thousandSeparator decimalScale={2} fixedDecimalScale />
                                    }
                                </Box>
                                <Box width="30%" textAlign="center" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                    <NumericFormat value={item.diff/100} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                                </Box>
                            </Stack>
                        </Box>
                    ))}
                </Box>
            </Stack>
            <Box sx={{ width: '35%', flexShrink: 0 }}>
                <Box sx={{ position: 'sticky', top: '65px', backgroundColor: 'white', borderRadius: 2, boxShadow: 2 }}>
                    <ItemDetails item={selectedItem?.item || null}>
                        {selectedItem?.item && (
                            <Button
                                variant='outlined'
                                size='small'
                                startIcon={<OpenInNew />}
                                onClick={() => window.open(`/procedures/${procedureId}/items/${selectedItem.item.id}`, '_blank', 'noopener,noreferrer')}
                            >
                                View Item Analysis
                            </Button>
                        )}
                    </ItemDetails>
                </Box>
            </Box>
        </Stack>
    );
}

export default CardUsageComparison;